import * as React from 'react';
import { Redistribution } from './Redistribution';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import { InputInfo } from 'sos-common/js/components/InputInfo/inputInfo';
import { CheckBoxState } from 'sos-common/js/components/FormControls/CheckBox';
import thousands from 'thousands';
import './_reqamount.scss';
import {
  calculationComponentState,
  calculationComponentProps,
  calculationComponentModel,
  calculationComponentModel2,
  ColumnDefinitions,
  ColumnTypeGroup,
  ColumnValue,
} from '../calculationComponents.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { getAsNumber, serializeComponent, valueObject } from '../calculationComponents.helpers';
import EventService from '../calculationComponents.eventservice';
import ValidationService from '../../../services/validation.service';

const AMOUNT_TO_REQUIRE = 'Belopp att rekvirera';
const REKVISITION_REQUESTED_AMOUNT = '__rekvisitionRequestedAmount';

export interface RequestedAmountProps extends calculationComponentProps {
  requestedAmountInfoText: string;
  iWantTheWholeSumText: string;
  iWantLesserSumText: string;
  showRedistribution: boolean;
  redistributionHeading: string;
  redistributionPreamble: string;
  iWantRedistribution: string;
  assignedAmount: number;
  formElementName: string;
}

export interface RequestedAmountState extends calculationComponentState {
  assignedAmount: number;
  assignedIsLocked: boolean;
  errorText: string;
  showRequestedAmountInfoText: boolean;
  iWantTheWholeSum: boolean;
  iWantRedistribution: boolean;
  jsonXmlInput: string;
}

export class RequestedAmount extends React.Component<RequestedAmountProps, RequestedAmountState> {
  private _errorText: string;
  constructor(props: RequestedAmountProps) {
    super(props);

    this._errorText = 'Beloppet får inte överstiga ' + thousands(props.assignedAmount, ' ') + ' kr.';
    let initialState = this.getInitialState();

    if (props.formElementJson) {
      initialState = this.getInitialStateFromJson(props.formElementJson);
    }

    this.state = initialState;
  }

  componentDidMount() {
    this.setState({
      jsonFields: this.getSerializedComponent(),
      jsonXmlInput: this.getSerializedForXmlComponent(),
    });

    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      this.setState({
        isTouched: true,
      });
    });
  }

  getInitialState = (): RequestedAmountState => {
    const { assignedAmount } = this.props;
    return {
      assignedAmount: assignedAmount ? assignedAmount : 0,
      isTouched: false,
      isValid: true,
      jsonFields: '',
      assignedIsLocked: true,
      errorText: '',
      showRequestedAmountInfoText: false,
      iWantTheWholeSum: true,
      iWantRedistribution: false,
      jsonXmlInput: '',
    };
  };

  getInitialStateFromJson = (json: string): RequestedAmountState => {
    const { assignedAmount } = this.props;

    try {
      let jsonData = JSON.parse(json) as calculationComponentModel;
      let values = jsonData.values[0];
      return {
        assignedAmount: values[1].value,
        isTouched: false,
        isValid: values[1].value <= assignedAmount,
        jsonFields: json,
        assignedIsLocked: values[3].value,
        errorText: values[1].value <= assignedAmount ? '' : this._errorText,
        showRequestedAmountInfoText: values[1].value === assignedAmount && !values[3].value,
        iWantTheWholeSum: values[3].value,
        iWantRedistribution: values[4].value,
        jsonXmlInput: '', //NOTE: no point in doing already here.
      };
    } catch (error) {
      console.warn('failed to parse JSON:', error);
      return this.getInitialState();
    }
  };

  getSerializedForXmlComponent = (): string => {
    const { formElementName, iWantLesserSumText } = this.props;

    const model = calculationComponentModel2.Create(formElementName, AMOUNT_TO_REQUIRE, 'Rekvirerat belopp');

    const columnValues = [
      { headerValue: iWantLesserSumText, value: (!this.state.iWantTheWholeSum).toString() } as ColumnValue,
      { headerValue: AMOUNT_TO_REQUIRE, value: thousands(this.state.assignedAmount, ' ') } as ColumnValue,
    ] as ColumnValue[];
    const columnTypeGroup = { columnValues } as ColumnTypeGroup;
    const columnTypeGroups = [columnTypeGroup] as ColumnTypeGroup[];
    model.columnDefinitions = { columnTypeGroups } as ColumnDefinitions;
    return JSON.stringify(model);
  };

  getSerializedComponent = () => {
    let value = [];
    let subheadings = null;
    if (this.props.showRedistribution) {
      value.push([
        valueObject(
          this.state.iWantTheWholeSum ? this.props.iWantTheWholeSumText : this.props.iWantLesserSumText,
          true
        ),
        valueObject(getAsNumber(this.state.assignedAmount), true),
        valueObject(this.state.iWantRedistribution ? 'Ja' : 'Nej', true),
        valueObject(this.state.iWantTheWholeSum, false),
        valueObject(this.state.iWantRedistribution, false),
      ]);
      subheadings = [null, AMOUNT_TO_REQUIRE, this.props.iWantRedistribution, null, null];
    } else {
      value.push([
        valueObject(
          this.state.iWantTheWholeSum ? this.props.iWantTheWholeSumText : this.props.iWantLesserSumText,
          true
        ),
        valueObject(this.state.assignedAmount, true),
        valueObject(null, false),
        valueObject(this.state.iWantTheWholeSum, false),
        valueObject(null, false),
      ]);
      subheadings = [null, AMOUNT_TO_REQUIRE, null, null, null];
    }
    return serializeComponent(null, value, null, this.props.formElementHeading, subheadings);
  };

  checkboxChanged = (state: CheckBoxState) => {
    this.setState(
      {
        iWantRedistribution: state.value,
      },
      () => {
        this.setState({
          jsonFields: this.getSerializedComponent(),
          jsonXmlInput: this.getSerializedForXmlComponent(),
        });
      }
    );
  };

  radioChanged = (field: FormField<any>) => {
    this.setState(
      (state, props) => {
        return {
          assignedIsLocked: field.value,
          showRequestedAmountInfoText: !field.value,
          assignedAmount: field.value ? props.assignedAmount : state.assignedAmount,
          isTouched: true,
          isValid: true,
          errorText: '',
          iWantTheWholeSum: field.value,
        };
      },
      () => {
        this.setState({
          jsonFields: this.getSerializedComponent(),
          jsonXmlInput: this.getSerializedForXmlComponent(),
        });
        if (!this.state.assignedIsLocked) {
          document.getElementById(REKVISITION_REQUESTED_AMOUNT).focus();
        }
      }
    );
  };

  amountChanged = (field: FormField<any>) => {
    let newAmount = getAsNumber(field.value);

    if (newAmount === this.props.assignedAmount) {
      this.setState(
        {
          showRequestedAmountInfoText: true,
          isValid: true,
          isTouched: true,
          assignedAmount: newAmount,
          errorText: '',
        },
        () => {
          this.setState({
            jsonFields: this.getSerializedComponent(),
            jsonXmlInput: this.getSerializedForXmlComponent(),
          });
        }
      );
      return;
    }

    if (newAmount > this.props.assignedAmount) {
      this.setState(
        {
          showRequestedAmountInfoText: false,
          isValid: false,
          isTouched: true,
          assignedAmount: newAmount,
          errorText: this._errorText,
        },
        () => {
          this.setState({
            jsonFields: this.getSerializedComponent(),
            jsonXmlInput: this.getSerializedForXmlComponent(),
          });
        }
      );
      return;
    }

    this.setState(
      {
        showRequestedAmountInfoText: false,
        isValid: true,
        isTouched: true,
        assignedAmount: newAmount,
        errorText: '',
      },
      () => {
        this.setState({
          jsonFields: this.getSerializedComponent(),
          jsonXmlInput: this.getSerializedForXmlComponent(),
        });
      }
    );
  };

  public render() {
    const {
      iWantLesserSumText,
      iWantTheWholeSumText,
      requestedAmountInfoText,
      redistributionHeading,
      redistributionPreamble,
      iWantRedistribution,
      showRedistribution,
    } = this.props;
    const redistributionProps = { redistributionHeading, redistributionPreamble, iWantRedistribution };

    return (
      <div>
        <div className="reqamount">
          <TextInput
            onChange={this.amountChanged}
            id={REKVISITION_REQUESTED_AMOUNT}
            readonly={this.state.assignedIsLocked}
            actAsNumber={true}
            fieldName={REKVISITION_REQUESTED_AMOUNT}
            label={AMOUNT_TO_REQUIRE}
            value={thousands(this.state.assignedAmount, ' ')}
            errorMessage={this.state.isTouched ? this.state.errorText : ''}
            isTouched={true}
          />
          {!this.state.assignedIsLocked && this.state.showRequestedAmountInfoText && requestedAmountInfoText && (
            <InputInfo infoText={requestedAmountInfoText} />
          )}
        </div>
        <RadioButtonGroup value={this.state.iWantTheWholeSum} onChange={this.radioChanged} fieldName="RequestedChoice">
          <RadioButton value={true} label={iWantTheWholeSumText} />
          <RadioButton value={false} label={iWantLesserSumText} />
        </RadioButtonGroup>

        {showRedistribution && (
          <Redistribution
            onChange={this.checkboxChanged}
            isChecked={this.state.iWantRedistribution}
            {...redistributionProps}
          />
        )}

        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={this.props.formElementName}
          id={this.props.formElementName}
          value={this.state.jsonFields}
        />
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={'__XmlInput' + this.props.formElementName}
          id={'__XmlInput' + this.props.formElementName}
          value={this.state.jsonXmlInput}
        />
      </div>
    );
  }
}
